import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAdminUsers, adminCreateUser } from '../../hooks/useAdminUsers';
import { AdminUserDetail } from './AdminUserDetail';

const STATUS_TABS = [
  { label: 'Todos', value: null },
  { label: 'Ativos', value: 'active' },
  { label: 'Trial', value: 'trial' },
  { label: 'Expirados', value: 'expired' },
  { label: 'Isentos', value: 'exempt' },
  { label: 'Banidos', value: 'banned' },
] as const;

const PER_PAGE = 25;

function getUserStatusBadge(user: { is_banned: boolean; is_exempt?: boolean; subscription_status: string | null; trial_ends_at: string | null }) {
  if (user.is_banned) return <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400">Banido</span>;
  if (user.is_exempt) return <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400">Isento</span>;
  if (user.subscription_status === 'active') return <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">Ativo</span>;
  if (user.trial_ends_at && new Date(user.trial_ends_at) > new Date()) return <span className="px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-400">Trial</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs bg-neutral-500/20 text-neutral-400">Expirado</span>;
}

function getPlanBadge(subscriptionStatus: string | null) {
  if (subscriptionStatus === 'active') return <span className="text-green-400 text-xs">Pro</span>;
  if (subscriptionStatus === 'trialing') return <span className="text-blue-400 text-xs">Trial</span>;
  return <span className="text-neutral-500 text-xs">Free</span>;
}

function formatRelativeDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR');
}

export const AdminUsers: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', exempt: false });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { users, total, loading, error, refresh } = useAdminUsers({
    search: debouncedSearch,
    status: statusFilter,
    page,
  });

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password) return;
    setCreateLoading(true);
    setCreateError(null);
    const { error } = await adminCreateUser(createForm.email, createForm.password, createForm.name || undefined, createForm.exempt || undefined);
    setCreateLoading(false);
    if (error) {
      setCreateError(error);
      return;
    }
    setCreateModalOpen(false);
    setCreateForm({ name: '', email: '', password: '', exempt: false });
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Usuários</h1>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Criar Usuário
        </button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-neutral-900 border border-white/10 rounded-lg text-base sm:text-sm text-white placeholder-neutral-500"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto whitespace-nowrap">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.label}
              onClick={() => { setStatusFilter(tab.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === tab.value
                  ? 'bg-white/10 text-white'
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 mb-4">
          Erro: {error}
        </div>
      )}

      {/* Table */}
      <div tabIndex={0} className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-neutral-400 text-left">
              <th className="p-4">Usuário</th>
              <th className="p-4">Email</th>
              <th className="p-4">Plano</th>
              <th className="p-4">Status</th>
              <th className="p-4">Forms</th>
              <th className="p-4">Criado em</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-neutral-500">
                  Nenhum usuário encontrado
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr
                  key={user.id}
                  onClick={() => setSelectedUserId(user.id)}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={user.avatar_url || '/default-avatar.svg'}
                        alt=""
                        className="w-8 h-8 rounded-full bg-neutral-800"
                        onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                      />
                      <span className="text-white">{user.full_name || '—'}</span>
                    </div>
                  </td>
                  <td className="p-4 text-neutral-400 max-w-[180px] truncate lg:max-w-none">{user.email}</td>
                  <td className="p-4">{getPlanBadge(user.subscription_status)}</td>
                  <td className="p-4">{getUserStatusBadge(user)}</td>
                  <td className="p-4 text-neutral-400">{user.forms_count}</td>
                  <td className="p-4 text-neutral-400">{formatRelativeDate(user.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-neutral-400">
          <span>Mostrando {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Detail Panel */}
      {selectedUserId && (
        <AdminUserDetail
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUpdated={refresh}
        />
      )}

      {/* Create User Modal */}
      {createModalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setCreateModalOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative bg-neutral-950 border border-white/10 rounded-2xl p-6 w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-white mb-4">Criar Usuário</h2>

            {createError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-sm text-red-400">
                {createError}
              </div>
            )}

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Nome completo"
                value={createForm.name}
                onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-500"
              />
              <input
                type="email"
                placeholder="Email *"
                value={createForm.email}
                onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-500"
              />
              <input
                type="password"
                placeholder="Senha temporária *"
                value={createForm.password}
                onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-500"
              />
              <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createForm.exempt}
                  onChange={e => setCreateForm(f => ({ ...f, exempt: e.target.checked }))}
                  className="rounded border-white/20"
                />
                Isentar de pagamento
              </label>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={handleCreateUser}
                disabled={!createForm.email || !createForm.password || createLoading}
                className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {createLoading ? 'Criando...' : 'Criar'}
              </button>
              <button
                onClick={() => { setCreateModalOpen(false); setCreateError(null); }}
                className="px-4 py-2 bg-white/5 text-neutral-400 rounded-lg text-sm hover:bg-white/10 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
